import React, { useState, useEffect } from 'react';
import { Shuffle, Loader2, AlertCircle } from 'lucide-react';
import { orderRegistrationService } from '../../../../services/orderRegistrationService';
import { GenerateRandomOrderRequest, Warehouse } from '../../../../types/orderRegistration';

interface RandomOrderDialogProps {
  onSuccess: (orderNo: number) => void;
}

export const RandomOrderDialog: React.FC<RandomOrderDialogProps> = ({ onSuccess }) => {
  const [numberOfLines, setNumberOfLines] = useState(5);
  const [maxQuantityPerLine, setMaxQuantityPerLine] = useState(50);
  const [allowItemsWithoutStock, setAllowItemsWithoutStock] = useState(false);
  const [warehouseId, setWarehouseId] = useState('');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      const data = await orderRegistrationService.getWarehouses();
      setWarehouses(data.filter(w => w.activeYesNo));

      // Set first warehouse as default
      if (data.length > 0 && data[0].activeYesNo) {
        setWarehouseId(data[0].warehouseID);
      }
    } catch (err) {
      console.error('Error loading warehouses:', err);
      setError('Kunne ikke laste lagerliste');
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (numberOfLines < 1 || numberOfLines > 100) {
      setError('Antall linjer må være mellom 1 og 100');
      return;
    }

    if (maxQuantityPerLine < 1 || maxQuantityPerLine > 10000) {
      setError('Maks antall per linje må være mellom 1 og 10000');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const request: GenerateRandomOrderRequest = {
        numberOfLines,
        allowItemsWithoutStock: allowItemsWithoutStock,
        warehouse: warehouseId || undefined,
        maxQuantityPerLine
      };

      const response = await orderRegistrationService.generateRandomOrder(request);

      if (response.success) {
        alert(`Testordre ${response.orderNo} opprettet med ${response.lines.length} linjer!`);
        onSuccess(response.orderNo);

        // Reset form
        setNumberOfLines(5);
        setMaxQuantityPerLine(50);
        setAllowItemsWithoutStock(false);
      } else {
        throw new Error(response.message || 'Kunne ikke generere ordre');
      }
    } catch (err) {
      console.error('Error generating random order:', err);
      setError(err instanceof Error ? err.message : 'Kunne ikke generere ordre. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingWarehouses) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-blue-600" size={32} />
        <span className="ml-3 text-gray-600">Laster data...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleGenerate} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      {/* Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Innstillinger for testordre</h3>

        <div className="space-y-4">
          {/* Number of Lines */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Antall ordrelinjer
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={numberOfLines}
              onChange={(e) => setNumberOfLines(parseInt(e.target.value) || 1)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">Mellom 1 og 100 linjer</p>
          </div>

          {/* Max Quantity Per Line Configuration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maks antall/quantity per ordrelinje
            </label>
            <input
              type="number"
              min="1"
              max="10000"
              value={maxQuantityPerLine}
              onChange={(e) => setMaxQuantityPerLine(parseInt(e.target.value) || 50)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">Setter maks antall per linje (mellom 1 og 10000)</p>
          </div>

          {/* Warehouse */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lager
            </label>
            <select
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Standard lager</option>
              {warehouses.map(w => (
                <option key={w.warehouseID} value={w.warehouseID}>
                  {w.warehouseDescr || w.warehouseID}
                </option>
              ))}
            </select>
          </div>

          {/* Allow Items Without Stock */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="allow-no-stock"
                type="checkbox"
                checked={allowItemsWithoutStock}
                onChange={(e) => setAllowItemsWithoutStock(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="allow-no-stock" className="text-sm font-medium text-gray-700">
                Tillat varer uten saldo
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Hvis avkrysset, kan varer uten tilgjengelig saldo inkluderes i testordren
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Om testordre-generering</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Systemet vil opprette en ordre med tilfeldige varer fra lageret</li>
              <li>Kunde og leveringsadresse vil bli generert automatisk</li>
              <li>Ordrenummer og leveringsdato settes automatisk</li>
              <li>Dette er nyttig for testing av plukk- og pakkeoperasjoner</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg transition-colors font-medium"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Genererer...
            </>
          ) : (
            <>
              <Shuffle size={20} />
              Generer Ordre
            </>
          )}
        </button>
      </div>
    </form>
  );
};
